import { useEffect, useState } from 'react';
import { Button, Col, Input, message, Modal, Row, Spin } from 'antd';
import { useActivate } from 'react-activation';
import api from '@/api/taskAcceptRequests';
import publicMethod from '@/utils/publicMethod';
import format from '@/utils/format';

interface TaskAcceptRequest {
    taskAcceptRequestId: string;
    taskId: string;
    taskTitle: string;
    taskState: string;
    taskerId: string;
    taskerName: string;
    taskerMobile?: string;
    actualRemunerationAmount?: string;
    workStartTime?: string;
    estimatedTime?: string;
    message?: string;
    createTime: string;
}

type Decision = 'approve' | 'decline';

const formatDuration = (minutes?: string) => {
    const total = Number(minutes);
    if (!total) return '-';
    const hours = Math.floor(total / 60);
    const rest = total % 60;
    if (!hours) return `${rest} min`;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

/**
 * Marketplace tasks a tasker has accepted. Accepting no longer assigns the task: it waits here
 * until an admin approves, and only then does the tasker see it as their task.
 */
const TaskApprovals = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<TaskAcceptRequest[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [confirm, setConfirm] = useState<{ open: boolean; decision: Decision; row: TaskAcceptRequest | null }>({
        open: false,
        decision: 'approve',
        row: null,
    });

    const getList = async () => {
        setLoading(true);
        try {
            const res = await api.getPending();
            setRows(res.data ?? []);
        } catch (error: any) {
            message.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getList();
    }, []);

    // KeepAlive-cached: requests arrive and get decided elsewhere, so refresh whenever the page is shown again.
    useActivate(getList);

    const openConfirm = (row: TaskAcceptRequest, decision: Decision) => {
        setDeclineReason('');
        setConfirm({ open: true, decision, row });
    };

    const closeConfirm = () => {
        setConfirm({ open: false, decision: 'approve', row: null });
        setDeclineReason('');
    };

    const submit = async () => {
        const { decision, row } = confirm;
        if (!row) return;
        setSubmitting(true);
        try {
            if (decision === 'approve') {
                await api.approve(row.taskId, row.taskerId);
                message.success(`${row.taskerName} is now assigned to "${row.taskTitle}"`);
            } else {
                await api.decline(row.taskId, row.taskerId, declineReason.trim() || undefined);
                message.success('Request declined');
            }
            closeConfirm();
        } catch (error: any) {
            message.error(error);
        } finally {
            setSubmitting(false);
            // Approving one request declines the others for the same task, and a row may already be
            // stale (task cancelled or decided elsewhere), so always reload.
            getList();
        }
    };

    const isApprove = confirm.decision === 'approve';

    return (
        <div className="page-main account">
            <div className="page-title">Task Approvals</div>
            <div className="select-bar">
                <div className="select-bar-left">
                    Taskers who accepted a marketplace task. The task starts only after you approve.
                </div>
                <Button onClick={getList} loading={loading}>
                    Refresh
                </Button>
            </div>
            <div className="table">
                {loading ? (
                    <div className="data_loading flex-center">
                        <Spin />
                    </div>
                ) : rows.length ? (
                    <>
                        <div className="table-th">
                            <Row wrap={false}>
                                <Col span={4}>Task</Col>
                                <Col span={4}>Tasker</Col>
                                <Col span={2}>Offer</Col>
                                <Col span={4}>Start time</Col>
                                <Col span={2}>Duration</Col>
                                <Col span={3}>Message</Col>
                                <Col span={2}>Requested</Col>
                                <Col span={3}>Actions</Col>
                            </Row>
                        </div>
                        {rows.map(row => (
                            <Row className="table-tr" wrap={false} key={row.taskAcceptRequestId}>
                                <Col span={4} title={row.taskTitle}>
                                    {row.taskTitle || '-'}
                                </Col>
                                <Col span={4}>
                                    <div>{row.taskerName || '-'}</div>
                                    <div>
                                        {row.taskerMobile ? window.utils.formatMobileNumber(row.taskerMobile) : '-'}
                                    </div>
                                </Col>
                                <Col span={2}>
                                    {row.actualRemunerationAmount
                                        ? `$${publicMethod.formatPrice(row.actualRemunerationAmount)}`
                                        : '-'}
                                </Col>
                                <Col span={4}>{format.formatTimeHMDY(row.workStartTime)}</Col>
                                <Col span={2}>{formatDuration(row.estimatedTime)}</Col>
                                <Col span={3} title={row.message}>
                                    {row.message || '-'}
                                </Col>
                                <Col span={2}>{format.formatTimeMDY(row.createTime)}</Col>
                                <Col span={3} style={{ display: 'flex', columnGap: 8 }}>
                                    <Button size="small" type="primary" onClick={() => openConfirm(row, 'approve')}>
                                        Approve
                                    </Button>
                                    <Button size="small" danger onClick={() => openConfirm(row, 'decline')}>
                                        Decline
                                    </Button>
                                </Col>
                            </Row>
                        ))}
                    </>
                ) : (
                    <div className="no-data flex-center">No task acceptances waiting for approval</div>
                )}
            </div>
            {/* The global theme hides antd Modal footers, so the buttons live in the body. */}
            <Modal width={560} maskClosable={false} centered open={confirm.open} onCancel={closeConfirm} footer={null}>
                <div className="modal-title">{isApprove ? 'Approve tasker' : 'Decline request'}</div>
                <div className="modal-tip tip-font-20">
                    {isApprove ? (
                        <>
                            Assign <strong>{confirm.row?.taskTitle}</strong> to{' '}
                            <strong>{confirm.row?.taskerName}</strong>? Any other requests for this task will be
                            declined.
                        </>
                    ) : (
                        <>
                            Decline <strong>{confirm.row?.taskerName}</strong> for{' '}
                            <strong>{confirm.row?.taskTitle}</strong>? The task keeps looking for a tasker.
                        </>
                    )}
                </div>
                {!isApprove && (
                    <Input.TextArea
                        style={{ marginTop: 16 }}
                        rows={3}
                        maxLength={500}
                        showCount
                        placeholder="Reason (optional)"
                        value={declineReason}
                        onChange={e => setDeclineReason(e.target.value)}
                    />
                )}
                <div className="flex-center" style={{ marginTop: 30, columnGap: 20 }}>
                    <Button className="submit-btn" onClick={closeConfirm}>
                        Cancel
                    </Button>
                    <Button
                        className="submit-btn"
                        type="primary"
                        danger={!isApprove}
                        loading={submitting}
                        onClick={submit}
                    >
                        {isApprove ? 'Approve' : 'Decline'}
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default TaskApprovals;
